import * as React from 'react'
import styled from 'styled-components'
import I18n from 'core/i18n'
import * as interfaces from 'static/interfaces'
import { sum } from 'lodash'
import * as constants from 'static/constants'
import Memo from './Drawer/Memo'
import { MemoModal } from './Drawer/MemoModal'
import {
  getTourGroupStyleClass,
  getGroupUnassignedTourGuidesByStatus,
  getGuideDisplayName,
  updatedTourByTourGroup,
} from 'utils/weekly'
import { TourGroupIcons } from 'components/molecules/Weekly/TourGroupIcons'
import { Tooltip } from 'components/atoms/Tooltip'
import { IBuildedTour, IJobSummary } from 'utils/weeklyTypes'
import { useWeeklyContext } from 'core/contexts/weekly/WeeklyContext'
import SvgIcon from 'components/atoms/SvgIcon'
import LinkableText from 'components/atoms/LinkableText'

declare var window: interfaces.IWindow

interface IProps {
  tour: IBuildedTour
  hideAllTourGroup: boolean
  handleClickTour: (tour: any) => void
}

const JOB_STATUS_LABELS: Record<string, string> = {
  open: '募集中',
  upcoming: '募集予定',
  ended: '募集終了',
}

const Tour: React.VFC<IProps> = React.memo(({ tour, hideAllTourGroup, handleClickTour }) => {
  const { onTourUpdate } = useWeeklyContext()
  const [hideTourGroup, setHideTourGroup] = React.useState(hideAllTourGroup)
  const [requestedGuides, approvedGuides, soundoutedGuides] = React.useMemo(() => {
    return getGroupUnassignedTourGuidesByStatus(tour.unAssignedTourGuides)
  }, [tour.unAssignedTourGuides])

  React.useEffect(() => {
    setHideTourGroup(hideAllTourGroup)
  }, [hideAllTourGroup])

  const onClick = () => {
    handleClickTour(tour)
  }

  const isPrivate = tour.post.post_type === 'private'

  const hasTodoGroupTours = tour.tour_groups.filter(tg => {
    // タスクが未完了またはアサインが未完了
    const assignedTourGroup = tg.assignedGuide !== null && tg.reservedNumber > 0
    return !tg.connected && !tg.allTaskDone // && assignedTourGroup
  })

  const todoLabel = hasTodoGroupTours
    .map(({ tasks }, i) => {
      const notDoneTasks = tasks.filter(todo => !todo.done)
      return notDoneTasks.length > 0
        ? `ツアーグループ${i + 1}: ${notDoneTasks.map(({ label }) => `[${label}]`)}\n`
        : ''
    })
    .join('\n')

  const connectedAll = tour.tour_groups.every(tg => tg.connected)

  const isCustomMemo = tour.memo_type === 'custom'

  // 複数募集は 募集中 > 募集予定 > 募集終了 > 募集非公開 の優先順で最優先の1件のみ表示する
  const displayedJobSummary = React.useMemo(() => {
    const priorities = { open: 0, upcoming: 1, ended: 2, draft: 3 }
    const effectiveStatus = (s: IJobSummary) => (!s.is_bulk && s.is_draft ? 'draft' : s.status)
    return [...tour.job_summaries].sort(
      (a, b) => priorities[effectiveStatus(a)] - priorities[effectiveStatus(b)]
    )[0]
  }, [tour.job_summaries])
  const displayedJobIsDraft =
    displayedJobSummary && !displayedJobSummary.is_bulk && displayedJobSummary.is_draft

  return (
    <Wrapper onClick={onClick}>
      <div
        className={`tour ${tour.showAlert && 'alert'} ${isPrivate ? 'private' : ''}`}
        key={tour.id}
      >
        {tour.showAlert && (
          <div className="addable">
            <Tooltip content="ツアーグループ増設可能">
              <SvgIcon name="tour-group-addable" width={26} height={26} />
            </Tooltip>
          </div>
        )}

        <TourGroupIcons
          tourGroups={tour.tour_groups}
          unassignedOtaReservations={tour.unassignedOtaReservations}
        />
        <p className="tourName">
          {tour.post.short_name || tour.post.name} ({tour.post.duration}h)
        </p>
        <div className="tourNum">
          <div className="reservedNumber" aria-label="ReservedNumber">
            {connectedAll || !isPrivate ? (
              <SvgIcon name="people" className="icon" ariaHidden={true} role="presentation" />
            ) : (
              <Tooltip content={todoLabel}>
                <span className="icons unlinked">
                  <SvgIcon name="unlinked" className="icon" ariaHidden={true} role="presentation" />
                  <SvgIcon
                    name="person_filled"
                    className="icon"
                    ariaHidden={true}
                    role="presentation"
                  />
                </span>
              </Tooltip>
            )}
            {tour.reservedNumber}
          </div>
          / {tour.maxNumber} (x{tour.tour_groups.length})
        </div>
        {displayedJobSummary && (
          <div
            className={`job ${displayedJobSummary.status} ${
              displayedJobSummary.is_bulk ? 'isBulk' : ''
            } ${displayedJobIsDraft ? 'isDraft' : ''}`}
          >
            <span>
              {displayedJobIsDraft
                ? '募集非公開'
                : `${displayedJobSummary.is_bulk ? '一斉' : ''}${
                    JOB_STATUS_LABELS[displayedJobSummary.status]
                  }`}
            </span>
            {displayedJobSummary.has_direct_slot && (
              <SvgIcon name="icon-direct" className="icon" ariaHidden={true} role="presentation" />
            )}
            {displayedJobSummary.has_selection_slot && (
              <SvgIcon
                name="icon-selection"
                className="icon"
                ariaHidden={true}
                role="presentation"
              />
            )}
          </div>
        )}
        <div className="tourMemo">
          {tour.memo ? (
            <MemoText isCustom={isCustomMemo}>
              <LinkableText>{tour.memo}</LinkableText>
            </MemoText>
          ) : (
            <Memo
              target={tour}
              targetClass="tour"
              internal={false}
              handleAfterUpdate={(target, params) => {
                onTourUpdate({ ...target, ...params })
              }}
            />
          )}
        </div>
        <div className={`${hideTourGroup && 'hide'}`}>
          <div className="tourGroups">
            {tour.tour_groups.map((tour_group, index) => {
              const tourGroupClassName = getTourGroupStyleClass({
                ...tour_group,
                isPrivate,
              })
              const guestName = tour_group.assignedOtaReservations
                .map(
                  otaReservation =>
                    otaReservation.custom_guest_name || otaReservation.customer_full_name
                )
                .filter(Boolean)
                .join(', ')

              const guideName = getGuideDisplayName(tour_group.assignedGuide)
              const privateNameLabel = guestName ? `${guideName} | ${guestName}` : guideName

              return (
                <div className={`tourGroup ${tourGroupClassName}`} key={tour_group.id}>
                  {isPrivate ? (
                    <div className="tourGroupInfo">
                      <span className="groupPrivateName">
                        <Tooltip content={privateNameLabel}>
                          <span className="privateNameLabel">
                            <span className="privateName guideName">{guideName}</span>
                            {guestName && (
                              <>
                                <span className="privateNameSeparator">|</span>
                                <span className="privateName">{guestName}</span>
                              </>
                            )}
                          </span>
                        </Tooltip>
                      </span>
                      <span className="groupNum">
                        <span className="groupReservedNumber">{tour_group.reservedNumber}</span>
                      </span>
                    </div>
                  ) : (
                    <div className="tourGroupInfo">
                      <span className="groupGuideName guideName">{guideName}</span>
                      <span className="groupNum">
                        <span className="groupReservedNumber">{tour_group.reservedNumber}</span>/{' '}
                        {tour_group.maxNumber}
                      </span>
                    </div>
                  )}
                  {(tour_group.memo || tour_group.internal_memo) && (
                    <div className="tourGroupHasMemo">{I18n.t('calendar.has_memo')}</div>
                  )}

                  <div
                    onClick={e => {
                      e.stopPropagation()
                      window.globalModal.showModal({
                        title: `ツアーグループ - メモ入力`,
                        body: (
                          <MemoModal
                            target={tour_group}
                            targetClass="tourGroup"
                            internal={true}
                            external={true}
                            handleAfterUpdate={(updated_tour_group, params) => {
                              onTourUpdate(
                                updatedTourByTourGroup(tour, updated_tour_group.id, params)
                              )
                            }}
                          />
                        ),
                        handleSubmit: undefined,
                        zIndex: 6001,
                      })
                    }}
                    role="button"
                    aria-label="write memo for tour group"
                    className="tourGroupMemoBtn"
                  >
                    <SvgIcon name="note" className="icon" ariaHidden={true} role="presentation" />
                  </div>
                </div>
              )
            })}
          </div>
          {requestedGuides.length > 0 && (
            <div className="unAssignedTourGuides requested">
              <div className="unAssignedTourGuidesInfo">
                <span>
                  {I18n.t('tour_guide_request_status.requested', { locale: 'ja' })}
                  {I18n.t('generic.guide', { locale: 'ja' })}
                </span>
              </div>
              {requestedGuides.map(({ guide }) => (
                <div className="unassignedGuide guideName" key={guide.id}>
                  {getGuideDisplayName(guide)}
                </div>
              ))}
            </div>
          )}
          {approvedGuides.length > 0 && (
            <div className="unAssignedTourGuides approved">
              <div className="unAssignedTourGuidesInfo">
                <span>
                  {I18n.t('tour_guide_request_status.approved', { locale: 'ja' })}
                  {I18n.t('generic.guide', { locale: 'ja' })}
                </span>
              </div>
              {approvedGuides.map(({ guide }) => (
                <div className="unassignedGuide guideName" key={guide.id}>
                  {getGuideDisplayName(guide)}
                </div>
              ))}
            </div>
          )}
          {soundoutedGuides.length > 0 && (
            <div className="unAssignedTourGuides soundouted">
              <div className="unAssignedTourGuidesInfo">
                <span>
                  {I18n.t('tour_guide_request_status.soundouted', { locale: 'ja' })}
                  {I18n.t('generic.guide', { locale: 'ja' })}
                </span>
              </div>
              {soundoutedGuides.map(({ guide }) => (
                <div className="unassignedGuide guideName" key={guide.id}>
                  {getGuideDisplayName(guide)}
                </div>
              ))}
            </div>
          )}
          {tour.unassignedOtaReservations.length > 0 && (
            <div className="unassignedReservations">
              <div className="title">{I18n.t('generic.other_guests', { locale: 'ja' })}</div>
              <div className="number">
                {sum(
                  tour.unassignedOtaReservations.map(otaReservation => otaReservation.num_of_people)
                )}
              </div>
            </div>
          )}
        </div>
        <div
          onClick={e => {
            e.stopPropagation()
            setHideTourGroup(!hideTourGroup)
          }}
          className="expandBtn"
          role="button"
          aria-label="Toggle tour detailed info"
        >
          {hideTourGroup ? (
            <SvgIcon name="expand_more" className="icon" ariaHidden={true} role="presentation" />
          ) : (
            <SvgIcon name="expand_less" className="icon" ariaHidden={true} role="presentation" />
          )}
        </div>
      </div>
    </Wrapper>
  )
})

const Wrapper = styled.div`
  letter-spacing: 0;
  margin-bottom: 1px;
  .tour {
    position: relative;
    line-height: 18px;
    padding: 8px;
    font-size: 13px;
    background: ${constants.COLORS.white};
    border: 1px solid ${constants.COLORS.subText};
    border-radius: 4px;
    cursor: pointer;
    &.alert {
      border: 1px solid ${constants.COLORS.primary};
      .tourGroups {
        margin: 4px -8px -8px;
        padding: 4px 8px 8px;
        border-radius: 0 0 3px 3px;
      }
    }
    &:hover {
      .expandBtn {
        opacity: 1;
        &:hover {
          transform: scale(1.1);
        }
      }
    }
    .addable {
      position: absolute;
      top: 0;
      right: 0;
    }
    .expandBtn {
      position: absolute;
      display: flex;
      align-items: center;
      justify-content: center;
      top: 4px;
      right: 4px;
      width: 28px;
      height: 28px;
      color: ${constants.COLORS.white};
      background-color: ${constants.COLORS.primary};
      border: 1px solid ${constants.COLORS.white};
      border-radius: 50%;
      transition: opacity 0.2s, transform 0.2s;
      opacity: 0;
      cursor: pointer;
      .icon {
        width: 20px;
        height: 20px;
      }
    }
    .tourName {
      margin-bottom: 2px;
      word-break: break-all;
    }
    .job {
      display: flex;
      align-items: center;
      gap: 2px;
      padding: 2px 4px;
      width: 100%;
      margin-top: 4px;
      font-size: 11px;
      border-radius: 2px;
      &.open,
      &.upcoming,
      &.ended {
        span {
          font-weight: bold;
        }
      }
      &.open {
        color: ${constants.COLORS.white};
        background-color: ${constants.COLORS.info2};
      }
      &.ended {
        color: ${constants.COLORS.subText};
        background-color: ${constants.COLORS.disabled};
      }
      &.upcoming {
        color: ${constants.COLORS.info2};
        background-color: ${constants.COLORS.infoBG2};
      }
      &.open.isBulk {
        color: ${constants.COLORS.white};
        background-color: ${constants.COLORS.primary};
      }
      &.upcoming.isBulk {
        color: ${constants.COLORS.primary};
        background-color: ${constants.COLORS.successBG2};
      }
      &.open.isDraft,
      &.ended.isDraft,
      &.upcoming.isDraft {
        color: ${constants.COLORS.subText};
        background-color: ${constants.COLORS.lightGray};
      }
      .icon {
        flex: 0 0 auto;
        width: 16px;
        height: 16px;
      }
      span {
        flex: 1 1 auto;
      }
    }
    .tourMemo {
      margin: 4px 0 0;
      line-height: 16px;
      color: ${constants.COLORS.subText};
      font-size: 10px;
      word-break: break-all;
      display: -webkit-box;
      -webkit-box-orient: vertical;
      -webkit-line-clamp: 3;
      overflow: hidden;
      .memoButton {
        margin-top: 0;
        margin-left: -3px;
      }
    }
    .tourNum {
      display: flex;
      align-items: center;
      color: ${constants.COLORS.subText};
      white-space: nowrap;
      .noNum {
        font-size: 9px;
        white-space: wrap;
        line-height: 14px;
        margin-top: 4px;
      }
      .reservedNumber {
        display: flex;
        align-items: center;
        margin-right: 4px;
        color: ${constants.COLORS.primary};
        font-weight: bold;
        .icons {
          margin-right: 4px;
          &.unlinked {
            color: ${constants.COLORS.subText};
          }
        }
        .icon {
          width: 18px;
          height: 18px;
        }
      }
      @media (max-width: ${constants.BREAKPOINT_DESKTOP_XXL}px) {
        .reservedNumber .icon {
          width: 16px;
          height: 16px;
        }
      }
      @media (max-width: ${constants.BREAKPOINT_DESKTOP_XL}px) {
        font-size: 12px;
        .reservedNumber .icons,
        .reservedNumber .icon {
          display: none;
        }
      }
    }
    .tourGroup {
      position: relative;
      display: flex;
      flex-direction: column;
      padding: 4px;
      margin-top: 4px;
      border-radius: 2px;

      .tourGroupInfo {
        display: flex;
        align-items: center;
        justify-content: space-between;
      }
      .tourGroupHasMemo {
        margin-top: 4px;
        font-size: 12px;
        color: ${constants.COLORS.subText};
      }
      .groupGuideName {
        flex: 1 1 auto;
        word-break: break-all;
      }
      .groupPrivateName {
        display: flex;
        flex: 1 1 auto;
        min-width: 0;
        > div {
          min-width: 0;
          max-width: 100%;
        }
        .privateNameLabel {
          display: flex;
          align-items: center;
          overflow: hidden;
          .privateName {
            min-width: 0;
            overflow: hidden;
            white-space: nowrap;
            text-overflow: ellipsis;

            &.guideName {
              display: -webkit-box;
              flex: 1 1 auto;
              white-space: normal;
              overflow-wrap: anywhere;
              word-break: break-word;
              -webkit-box-orient: vertical;
              -webkit-line-clamp: 2;
            }
          }
          .privateNameSeparator {
            flex: 0 0 auto;
            margin: 0 4px;
          }
        }
      }
      .guideName {
        display: -webkit-box;
        flex: 1 1 auto;
        min-width: 0;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: normal;
        overflow-wrap: anywhere;
        word-break: break-word;
        -webkit-box-orient: vertical;
        -webkit-line-clamp: 2;
      }
      .groupNum {
        flex: 0 0 auto;
        padding-left: 4px;
        color: ${constants.COLORS.subText};
        .groupReservedNumber {
          margin-right: 4px;
          color: ${constants.COLORS.primary};
          font-weight: bold;
        }
      }
      &.ok {
        background-color: ${constants.COLORS.successBG2} !important;
      }
      &.caution {
        background-color: ${constants.COLORS.successBG2} !important;
        .groupNum {
          .groupReservedNumber {
            color: ${constants.COLORS.primary};
          }
        }
      }
      &.alert {
        background-color: ${constants.COLORS.tourGuideSoundoutBG} !important;
      }
      &.ng {
        color: ${constants.COLORS.danger};
        background-color: ${constants.COLORS.tourGuideMissingBG} !important;
      }
      &.empty {
        background-color: ${constants.COLORS.lightGray} !important;
        .groupNum {
          .groupReservedNumber {
            color: ${constants.COLORS.subText};
          }
        }
      }
      .tourGroupMemoBtn {
        position: absolute;
        display: flex;
        align-items: center;
        justify-content: center;
        top: 2px;
        left: 2px;
        width: 22px;
        height: 22px;
        border-radius: 2px;
        color: ${constants.COLORS.white};
        background-color: ${constants.COLORS.primary};
        transition: opacity 0.2s, transform 0.2s;
        opacity: 0;
        cursor: pointer;
        .icon {
          width: 16px;
          height: 16px;
        }
      }
      &:hover {
        .tourGroupMemoBtn {
          opacity: 1;
          &:hover {
            transform: scale(1.1);
          }
        }
      }
    }
    &.private {
      .tourGroup {
        &.connected {
          background-color: ${constants.COLORS.privateTourConnectedBG} !important;
        }
        &.guideAndGuest {
          background-color: ${constants.COLORS.privateTourGuideAndGuestBG} !important;
        }
      }
    }
    .unAssignedTourGuides {
      line-height: 16px;
      padding: 4px;
      margin-top: 4px;
      font-size: 12px;
      border-radius: 2px;
      &.requested,
      &.approved,
      &.soundouted {
        background-color: ${constants.COLORS.lightGray};
      }
      .unAssignedTourGuidesInfo {
        display: flex;
        align-items: center;
        justify-content: space-between;
        color: ${constants.COLORS.subText};
        font-weight: bold;
        > span {
          flex: 1 1 auto;
          &:last-child {
            flex: 0 0 auto;
            padding-left: 4px;
          }
        }
      }

      &.soundouted {
        .unAssignedTourGuidesInfo {
          color: ${constants.COLORS.tourGuideSoundoutText};
        }
      }

      .unassignedGuide {
        margin-top: 4px;
        word-break: break-all;
        &::before {
          content: '-';
          margin-right: 4px;
        }
      }
      .unassignedGuide.guideName {
        display: -webkit-box;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: normal;
        overflow-wrap: anywhere;
        word-break: break-word;
        -webkit-box-orient: vertical;
        -webkit-line-clamp: 2;
      }
    }
    .unassignedReservations {
      display: flex;
      align-items: center;
      justify-content: space-between;
      line-height: 12px;
      padding: 4px;
      margin-top: 8px;
      color: ${constants.COLORS.primary};
      font-size: 12px;
      background-color: ${constants.COLORS.white};
      border: dashed 1px ${constants.COLORS.primary};
      border-radius: 2px;
      .number {
        font-weight: bold;
      }
    }
    .hide {
      display: none;
    }
  }
`

const MemoText = styled.span<{ isCustom: boolean }>`
  ${({ isCustom }) =>
    isCustom &&
    `
    border-left: 3px solid ${constants.COLORS.info4};
    padding-left: 6px;
  `}
`

export default Tour
